// Office/History:

// {{{ CDR.UnavailableRecordingDialog

GW.define('CDR.UnavailableRecordingDialog', 'Main.Dialog', {
	title: _('Nahrávka nedostupná'),
	subtitle: _('Tato nahrávka již v úložišti není dostupná'),

	'after:initComponent'() {
		this.footerEl.gwReplaceChildren([{
			className: 'form-buttons',
			children:[{
				nodeName: 'button',
				className: 'primary',
				textContent: _('Rozumím'),
				'on:click': () => {
					this.destroy();
				},
			}],
		}]);
	},
});

// }}}
// {{{ AudioPlayer

GW.define('AudioPlayer', 'GW.Component', {

	// duration of the audio in seconds. Used before the audio is loaded.
	durationSeconds: null,
	// If false, the audio is loaded on first click on play button.
	preloadMetadata: true,

	initComponent() {
		this.el = document.gwCreateElement({
			children: [{
				ref: 'audioEl',
				nodeName: 'audio',
				'on:play': () => this.updateState(),
				'on:ended': () => this.updateState(),
				'on:pause': () => this.updateState(),
				'on:loadedmetadata': () => this.onLoad(),
				'on:error': e => this.onLoadError(e),
			}, {
				ref: 'playBtn',
				nodeName: 'button',
				type: 'button',
				className: 'icon-only play-button',
				children: [{
					ref: 'iconWrapEl',
					className: 'icon-wrapper',
					children: [Utils.useIcon('playfilled')]
				}],
				'on:click': async () => {
					const disableDial = Application.getActiveUserGroups().every(g => g.options.recording_play_disable_dial);
					if (this.audioDialUrl && !disableDial && await this.selectedDeviceIsNotWebRTC()) {
						await REST.POST(this.audioDialUrl);
					} else {
						await this.play();
					}
				},
			}, {
				ref: 'statusTime',
				className: 'status-time',
				textContent: '–:––',
			}, {
				nodeName: 'input',
				type: 'range',
				ref: 'range',
				className: 'progress-range',
				'on:change': e => this.audioEl.currentTime = e.target.value,
				disabled: true,
				min: 0,
				value: 0,
				step: 0.1,
			}, {
				ref: 'statusDuration',
				className: 'status-duration',
				textContent: '–:––',
			}],
		}, this);

		this.monDom(this.audioEl, 'timeupdate', e => this.onTimeUpdate(e));

		// if requested, preload metadata on component init, otherwise defer loading to first play button click
		if (this.preloadMetadata) {
			this.audioEl.preload = 'metadata';
			this.audioEl.src = this.audioUrl;
		}

		// prefill the timings if known before loading the audio
		if (this.durationSeconds !== null && this.durationSeconds !== undefined) {
			this.statusTime.textContent = Utils.secondsToHoursMinutes(0, true);
			this.statusDuration.textContent = Utils.secondsToHoursMinutes(this.durationSeconds, true);
		}
	},

	releaseComponent() {
		this.audioEl.pause();
		this.audioEl.removeAttribute('src');
	},

	$key_ArrowLeft(e) {
		e.preventDefault();
		this.seekRelative(-5);
	},

	$key_ArrowRight(e) {
		e.preventDefault();
		this.seekRelative(5);
	},

	$key_Escape(ev) {
		this.stop();
		this.audioEl.currentTime = 0;
	},

	onLoad() {
		this.statusTime.textContent = Utils.secondsToHoursMinutes(this.audioEl.currentTime || 0, true);
		this.statusDuration.textContent = Utils.secondsToHoursMinutes(this.audioEl.duration || 0, true);
		
		if (this.audioEl.seekable) {
			this.range.max = this.audioEl.duration;
			this.range.defaultValue = 0;
			this.range.value = 0;
			this.range.disabled = false;
		}
		this.loaded = true;
	},

	onLoadError(e) {
		if (e.target.error.MEDIA_ERR_SRC_NOT_SUPPORTED) {
			this.statusDuration.textContent = 'Unsupported media';
			console.error('Unsupported format', e.target.src);
		}
		this.updateState();
	},

	onTimeUpdate(e) {
		this.statusTime.textContent = Utils.secondsToHoursMinutes(e.target.currentTime || 0, true);

		if (!this.audioEl.seeking) {
			this.range.value = e.target.currentTime;
		}
	},

	updateState() {
		let active = !this.audioEl.paused && !this.audioEl.ended && !this.audioEl.error;

		this.iconWrapEl.gwReplaceChildren(
			[!active ? Utils.useIcon('playfilled') : Utils.useIcon('pause')]
		);
		// When user seeks via keyboard very fast, audio `ended` event
		// doesn't fire (just paused event is fired)
		if (this.audioEl.ended) {
			setTimeout(() => {
				this.range.value = 0;
				this.audioEl.currentTime = 0;
			}, 250);
		}
	},

	async play() {
		this.fire('beforeplay');

		let audio = this.audioEl;
		if (!this.loaded) {
			// defer HTTP request to first play attempt
			audio.src = this.audioUrl;
		}

		try {
			if (audio.paused) {
				// Try to recover from fast seeking via keyboard
				if (audio.readyState < 4) {
					audio.load();
				}

				await audio.play();
			} else {
				audio.pause();
			}
		} catch (ex) {
			if (audio.error && audio.error.message.match(/404/)) {
				Application.notify({
					kind: 'error',
					text: _('Soubor s nahrávkou již neexistuje'),
				});
				return;
			}

			throw ex;
		}
	},

	seekRelative(diff) {
		let audio = this.audioEl;

		if (!audio.seeking && audio.currentTime + diff < audio.duration) {
			audio.currentTime = Math.min(Math.max(audio.currentTime + diff, 0), audio.duration);
		}
	},

	stop() {
		this.audioEl.pause();
	},

	async selectedDeviceIsNotWebRTC() {
		// explicit dialer/state is needed since the AudioPlayer may be in
		// application without preloaded dialer state (like CDR)
		const dialer_state = await REST.GET('dialer/state');
		const selected_device = Office.CallManager.active_device;

		return dialer_state.devices.some(d => d.kind !== 'webrtc') && selected_device != 'webrtc';
	}
});

// }}}
// {{{ CDR.CallRecordingsTable

GW.define('CDR.CallRecordingsTable', 'SmartTable', {
	emptyText: _('Tento hovor nemá žádné nahrávky'),
	recordingsBaseUrl: null,

	getColumns() {
		let me = this;

		return [{
			id: 'caller',
			name: _('Volající'),
		}, {
			id: 'caller_name',
			name: _('Jméno'),
		}, {
			id: 'callee',
			name: _('Volaný'),
		}, {
			id: 'callee_name',
			name: _('Jméno'),
		}, {
			id: 'start_ts',
			name: _('Začátek záznamu'),
			format: 'datetime',
		}, {
			id: 'duration',
			name: _('Délka záznamu'),
			format: 'duration_unpadded',
		}, {
			id: 'ctl',
			name: _('Akce'),
			formatCell(td, v, row) {
				if (!row.readable) {
					td.textContent = _('Záznam nedostupný');
					return;
				}

				td.gwCreateChild({
					xtype: 'AudioPlayer',
					audioUrl: me.recordingsBaseUrl + row.recording_id,
					audioDialUrl: me.recordingsBaseUrl + row.recording_id + '/dial',
					'on:beforeplay': c => {
						me.stopAllRecordings(c);
					},
				});

				if (Application.session.user.superadmin || Application.getActiveUserGroups()
						.some(g => !['regular', 'agent'].includes(g.member_role) || (g.options.show_recordings && g.options.allow_recordings_download))
				) {
					td.gwCreateChild({
						nodeName: 'a',
						className: 'button small secondary icon-only',
						children: [Utils.useIcon('download')],
						href: me.recordingsBaseUrl + row.recording_id + '?download=1',
						download: '',
					});
				}
			}
		}];
	},

	stopAllRecordings(except) {
		this.getChildren(c => c !== except && c instanceof AudioPlayer, true)
			.forEach(c => c.stop());
	},
});

// }}}
// {{{ CDR.CallRecordingsDialog

GW.define('CDR.CallRecordingsDialog', 'Main.Dialog', {
	title: _('Nahrávky'),
	
	showCallRatings: true,

	'after:initComponent'() {
		this.bodyEl.gwReplaceChildren([{
			ref: 'table',
			xtype: 'CDR.CallRecordingsTable',
			recordingsBaseUrl: this.recordingsBaseUrl,
		}, {
			':skip': !this.showCallRatings || !Application.getActiveUserGroups().some(g => ['admin', 'manager', 'supervisor'].includes(g.member_role)) || !this.call_id,
			nodeName: 'h5',
			className: 'mb-3',
			textContent: _('Hodnocení'),
		}, {
			ref: 'ratings',
			':skip': !this.showCallRatings || !Application.getActiveUserGroups().some(g => ['admin', 'manager', 'supervisor'].includes(g.member_role)) || !this.call_id,
			xtype: 'CDR.CallRatings',
			call_id: this.call_id
		}], this);
	},

	setRecordings(recs) {
		this.table.setData(recs);
		if (this.ratings) {
			let refUsers = new Map();
			
			const add_user = (id, exten, name) => refUsers.set(id, {id, exten, name});
			
			for (let rec of recs) {
				if (rec.caller_user_id) {
					add_user(rec.caller_user_id, rec.caller, rec.caller_name);
				}
				if (rec.callee_user_id) {
					add_user(rec.callee_user_id, rec.callee, rec.callee_name);
				}
			}
			
			// TODO: limit refUsers only to [caller_user_id, callee_user_id, last_callee_user_id]
			//       because other users do not have access to the dbc_call via the ACL anyway

			this.ratings.setRefUsers([...refUsers.values()]);
		}
	},
});


GW.define('CDR.CallVoicemailsDialog', 'Main.Dialog', {
	title: _('Záznamník u hovoru'),

	'after:initComponent'() {
		this.bodyEl.gwCreateChild({
			ref: 'table',
			xtype: 'Office.VoicemailsTable',
		}, this);
	},

	setVoicemails(voicemails) {
		this.table.setData(voicemails);
	},
});

GW.define('CDR.CallRatings', 'GW.Component', {
	
	call_id: null,

	'after:initComponent'() {
		this.el = document.gwCreateElement({
			className: 'call-ratings-form',
			children: [{
				ref: 'usersTabs',
				xtype: 'Widgets.TabSwitcher',
				'on:beforeselect': (c, i) => {
					this.selectRefUser(i.id);
				},
			}, {
				ref: 'cardSwitcher',
				xtype: 'Widgets.CardSwitcher',
			}],
		}, this);
	},
	
	selectRefUser(ref_user_id) {
		this.usersTabs.setSelected(ref_user_id);
		if (ref_user_id) {
			this.cardSwitcher.show({
				id: ref_user_id,
				xtype: 'CDR.CallRating',
				call_id: this.call_id,
				ref_user_id
			});
		} else {
			this.cardSwitcher.show({
				id: 'no_ref_user',
			});
		}
	},
	
	setRefUsers(refUsers) {
		this.usersTabs.setItems(refUsers.map(u => ({
			id: u.id,
			title: u.exten + ' - ' + u.name,
		})));
		if (refUsers.length > 0) {
			this.selectRefUser(refUsers[0].id); 
		}
	},

});

GW.define('CDR.CallRating', 'GW.Component', {
	
	call_id: null,
	ref_user_id: null,

	call_rating: {},

	'after:initComponent'() {
		const me = this;
		this.el = document.gwCreateElement({
			className: 'call-ratings-form',
			children: [{
				className: 'rate-toolbar',
				children: [{
					xtype: 'Widgets.ButtonSwitcher',
					cls: 'button-switcher large separate light radios' + (Application.getActiveUserGroups().some(g => ['supervisor', 'manager'].includes(g.member_role)) ? '' : ' static-active'),
					ref: 'ratingEl',
					items: ['-1', '0', '1'].map(v => ({
						id: v,
						children: [Formats.call_rating_icon(v), {textContent: Formats.call_rating_text(v)}],
					})),
					'on:beforeselect': async (c, i) => {
						await REST.POST(`calls/${this.call_id}/rating/${this.ref_user_id}/rate`, {
							rating: Number(i.id),
						});
						this.reloadCallRating();
					},
				}, {
					xtype: 'AckCheckboxField',
					ref: 'confirmedEl',
					shortText: _('Přečteno'),
					optional: true,
					disabled: !(Application.getActiveUserGroups()
							.some(g => ['supervisor', 'manager', 'admin'].includes(g.member_role)) ||
						Application.session.user.id == this.ref_user_id),
					'on:change': async el => {
						if (el.getValue()) {
							await REST.POST(`calls/${this.call_id}/rating/${this.ref_user_id}/confirm`);
						} else {
							await REST.POST(`calls/${this.call_id}/rating/${this.ref_user_id}/unconfirm`);
						}
						this.reloadCallRating();
					},
				}]
			}, {
				className: 'thread-content-container',
				children: [{
					className: 'thread-container',
					ref: 'commentsEl',
				}, {
					xtype: 'Widgets.SmartTextArea',
					ref: 'smsInput',
					autofocus: true,
					
					renderToolbar() {
						const smartTextArea = this;
						return [{
							className: 'textarea-toolbar',
							children: [{
								ref: 'sendButton',
								disabled: true, // initially disabled for an empty message
								nodeName: 'button',
								className: 'primary icon-left',
								'on:click': () => {
									smartTextArea.disableWhile(async () => {
										try {
											this.sendButton.disabled = true;
											await REST.POST(`calls/${me.call_id}/rating/${me.ref_user_id}/comment`, {text: smartTextArea.getText()});
											me.reloadCallRating();
										} finally {
											this.sendButton.disabled = false;
										}
									});
								},
								children: [Utils.useIcon('send'), {textContent: _('Odeslat')}],
							}]
						}];
					},
					
					'on:change'() {
						this.sendButton.disabled = !this.validate();
					},
				}]
			}]
		}, this);
		this.reloadCallRating();
	},
	
	async reloadCallRating() {
		const signal = Utils.abortAndRetry(this, 'reload');
		this.call_rating = (await REST.GET(`calls/${this.call_id}/rating/${this.ref_user_id}`, null, {signal})) || {};
		this.updateData();
	},
	
	updateData() {
		this.ratingEl.setSelected("" + this.call_rating.rating);
		if (this.call_rating.confirmed_ts) {
			// TODO show confirmed_by_user and the time
			this.confirmedEl.setValue(true);
		} else {
			this.confirmedEl.setValue(false);
		}
		this.commentsEl.gwReplaceChildren((this.call_rating.comments || []).map(c => ({
			className: 'thread-message ' + (c.author.id == Application.session.user.id ? 'thread-message-sent' : 'thread-message-received'),
			children: [{
				className: 'contact-icon',
				children: [Utils.useIcon('user')],
			}, {
				className: 'thread-content',
				children: [{
					className: 'message-header',
					children: [{
						className: 'message-from',
						textContent: c.author.name,
					}, {
						className: 'message-sent-at',
						textContent: Formats.datetime(c.ts)
					}],
				}, {
					className: 'message-text',
					textContent: c.text,
				}],
			}],
		})));

		this.commentsEl.scrollTop = this.commentsEl.scrollHeight;
	}
	
});

// }}}
// {{{ CDR.CallCategoryDialog

GW.define('CDR.CallCategoryDialog', 'Main.FormDialog', {
	title: _('Změnit kategorii'),
	hierarchy: null,
	disableConfirmIfNoneSelected: false,

	'before:initComponent'() {
		this.title += ' - ' + this.hierarchy.name;
	},

	'after:initComponent'() {
		this.el.classList.add('cdr-call-category-change-dialog');

		this.categoriesField.setCategoryList(this.hierarchy.categories);
		this.focus();

		if (this.disableConfirmIfNoneSelected) {
			// initially disabled, enable on category set if needed
			this.buttonConfirm.disabled = true;
		}
	},

	renderFormFields() {
		return [{
			ref: 'categoriesField',
			name: 'category',
			xtype: 'Office.CallPanel.CategorySelector',
			'on:change': el => {
				if (this.disableConfirmIfNoneSelected) {
					this.buttonConfirm.disabled = el.getValue() == null; 
				}
			},
			validate() {
				return true;
			},
		}];
	},

	getSelectedCategories() {
		return this.categoriesField.getSelectedCategories();
	},
});

// }}}
// {{{ CDR.HistoryScreen

GW.define('CDR.HistoryScreen', 'Main.Screen', {
	title: _('CDR'),
	titleIcon: 'phone-calls-filled',
	full: false,

	'after:initComponent'() {

		this.cdrTableColumnsSelector = Features.tableColumnsSelector({
			localStorageKey: 'cdr-columns',
			getAllColumns: () => this.getAllColumns(),
			onSave: () => this.table.refresh()
		});

		this.el.classList.add('cdr-history-screen');
		this.bodyEl.gwReplaceChildren([{
			ref: 'toolbarEl',
			nodeName: 'form',
			'on:submit': ev => {
				ev.preventDefault();
				this.reload();
			},
			className: 'toolbar',
			children: this.renderToolbar(),
		}, {
			ref: 'table',
			xtype: 'FilterableSmartTable',
			extraClass: 'panel-container no-padding cdr-smart-table',
			getColumns: () => this.cdrTableColumnsSelector.getSelectedColumns(),
			renderRow(tr, row) {
				tr.dataset.direction = row.direction || '';
				tr.dataset.result = row.result || '';
			},
		}], this);

		setTimeout(() => {
			// help firefox
			let el = this.el.querySelector('[autofocus]');
			el && el.focus();
		}, 0);

		for (let f of this.getLiveToolbarSearchFields()) {
			f.on('change', c => this.updateParams());
		}
	},

	getLiveToolbarSearchFields() {
		return this.getChildren(c => {
			return c.fromParams && c.toParams && c.toQuery && c.el.closest('.toolbar');
		});
	},

	/*
	 * PLUGIN: extension point
	 */
	getColumns() {
		return [{
			id: 'direction',
			name: _('Směr'),
			formatCell(td, v, row) {
				td.gwCreateChild(Office.renderDirectionIcon(row, true));
				td.gwCreateChild(Formats.call_direction(v));
			},
			filter: {
				type: 'options',
			},
			sortable: {
				type: 'string',
				value: v => Formats.call_direction(v),
			}
		}, {
			id: 'start',
			format: 'datetime',
			name: _('Začátek hovoru'),
			sortable: {
				type: 'numeric',
			},
		}, {
			id: 'early_duration',
			format: 'duration',
			name: _('Čas v IVR'),
			filter: {
				type: 'duration',
			},
			sortable: {
				type: 'numeric'
			},
		}, {
			id: 'ring_duration',
			format: 'duration',
			name: _('Délka vyzvánění'),
			filter: {
				type: 'duration',
			},
			sortable: {
				type: 'numeric'
			},
		}, {
			id: 'duration',
			format: 'duration',
			name: _('Délka hovoru'),
			filter: {
				type: 'duration',
			},
			sortable: {
				type: 'numeric'
			},
		}, {
			id: 'queue',
			name: _('Vstupní fronta'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'last_queue',
			name: _('Poslední fronta'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'caller',
			name: _('Volající'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'caller_name',
			name: _('Jméno'),
			fullName: _('Jméno volajícího'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'caller_company',
			name: _('Firma'),
			fullName: _('Firma volajícího'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'caller_alias',
			name: _('Alias'),
			fullName: _('Alias volajícího'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'callee',
			name: _('Volaný'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'callee_name',
			name: _('Jméno'),
			fullName: _('Jméno volaného'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'callee_company',
			name: _('Firma'),
			fullName: _('Firma volaného'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'last_callee',
			name: _('Poslední volaný'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'last_callee_name',
			name: _('Jméno'),
			fullName: _('Jméno posledního volaného'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'last_callee_company',
			name: _('Firma'),
			fullName: _('Firma posledního volaného'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'transfer',
			name: _('Přepojení'),
			format: 'transfer_type',
			filter: {
				type: 'options',
			},
			sortable: {
				type: 'string',
				value: v => Formats.transfer_type(v),
			},
		}, {
			id: 'did',
			name: _('Vstupní číslo'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'result',
			name: _('Výsledek hovoru'),
			format: 'call_result',
			filter: {
				type: 'options',
			},
			sortable: {
				type: 'string',
				value: v => Formats.transfer_type(v),
			},
		}, {
			id: 'source',
			name: _('Zdroj'),
			filter: {
				type: 'options',
				showSearch: true,
			},
			sortable: {
				type: 'string'
			}
		}, {
			id: 'category1',
			name: _('Kategorie 1'),
			filter: {
				type: 'options',
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'category2',
			name: _('Kategorie 2'),
			filter: {
				type: 'options',
			},
			sortable: {
				type: 'string',
			},
		}, {
			id: 'hangup_by',
			name: _('Kdo ukončil hovor'),
			format: 'call_hangup_by',
			filter: {
				type: 'options',
			},
			sortable: {
				type: 'string'
			}
		}];
	},

	getAllColumns() {
		let me = this;

		if (this.full) {
			return [
				{
					id: 'id',
					name: _('Call ID'),
				},
				...this.getColumns(),
				{
					id: 'acl',
					name: _('ACL'),
					formatCell(td, v, row) {
						if (!v || v.length == 0) {
							return;
						}

						td.gwCreateChild({
							nodeName: 'button',
							className: 'small secondary icon-only btn-notes',
							children: [Utils.useIcon('document')],
							title: _('Zobrazit ACL'),
							'on:click': async ev => {
								me.showACLDialog(v);
							},
						});
					},
				},
				{
					id: 'detail',
					name: _('Detail'),
					formatCell(td, v, row) {
						td.gwCreateChild({
							nodeName: 'button',
							className: 'small secondary icon-only',
							children: [{
								textContent: 'D',
							}],
							title: _('Zobrazit časovou osu hovoru'),
							'on:click': async ev => {
								me.showCallDetailDialog(row.id);
							},
						});
					},
				},
				this.getRecordingAndVoicemailColumnForFullCdr(),
			];
		}

		return this.getColumns().concat(this.getDetailsColumn());
	},

	async showACLDialog(acl) {
		let users = await REST.GET('users');
		let groups = await REST.GET('user-groups');
		let userMap = new Map(users.map(v => [String(v.id), v.name]));
		let groupMap = new Map(groups.map(v => [String(v.id), v.name]));

		let rows = acl.map(n => {
			let m;
			if (m = n.match(/^G(\d+)$/)) {
				return {
					id: n,
					map: groupMap.get(m[1]) || '-',
				};
			} else if (m = n.match(/^M(\d+)\/(\d+)$/)) {
				return {
					id: n,
					map: (groupMap.get(m[1]) || '-') + ' / ' + (userMap.get(m[2]) || '-'),
				};
			} else if (m = n.match(/^U(\d+)$/)) {
				return {
					id: n,
					map: userMap.get(m[1]) || '-',
				};
			}

			return {id: n};
		});

		new Main.Dialog({
			title: _('ACL hovoru'),

			'after:initComponent'() {
				this.bodyEl.gwCreateChild({
					xtype: 'SmartTable',
					getColumns() {
						return [{
							id: 'id',
							name: _('ID'),
						}, {
							id: 'map',
							name: _('Jméno'),
						}];
					},
					rows,
				});
			},
		});
	},

	async showCallDetailDialog(call_id) {
		const conns = await REST.GET(`calls/${call_id}/connections`);

		const items = [];
		for (let conn of conns) {
			if (conn.dial_ts) {
				items.push({conn, ts: conn.dial_ts, type: 'dial'});
			}
			if (conn.ring_ts) {
				items.push({conn, ts: conn.ring_ts, type: 'ring'});
			}
			if (conn.start_ts) {
				items.push({conn, ts: conn.start_ts, type: 'start'});
			}
			if (conn.end_ts) {
				items.push({conn, ts: conn.end_ts, type: 'end'});
			}
			if (conn.queue_join_ts) {
				items.push({conn, ts: conn.queue_join_ts, type: 'queue_join'});
			}
		}
		items.sort((a, b) => a.ts - b.ts);

		new Main.Dialog({
			title: _('Detail hovoru - časová osa'),

			'after:initComponent'() {
				this.bodyEl.gwCreateChild({
					xtype: 'SmartTable',
					getColumns() {
						return [{
							id: 'ts',
							name: _('Čas'),
							format: 'time_millis',
						}, {
							id: 'type',
							name: _('Událost'),
							formatCell: (td, v, row) => {
								td.textContent = {
									dial: row.conn.external === 'caller' ? 
										_('Začátek vytáčení (vstup do ústředny)') : 
										_('Začátek vytáčení'),
									queue_join: _('Vstup do fronty'),
									ring: _('Začátek zvonění'),
									start: _('Spojení hovoru'),
									end: _('Konec hovoru'),
								}[v] || v;
							}
						}, {
							id: 'caller',
							name: _('Volající'),
							formatCell: (td, v, row) => {
								td.gwReplaceChildren([{
									textContent: row.conn.caller,
								}, {
									':skip': !row.conn.caller_user_name,
									textContent: row.conn.caller_user_name,
								}, {
									className: 'small',
									textContent: row.conn.external === 'caller' || row.conn.external === 'both' ? _('Externí') : _('Interní'),
								}, {
									className: 'small',
									textContent: ((/@([^:]+)/.exec(row.conn.caller_device_uri || '')) || [])[1],
									title: row.conn.caller_device_uri,
								}, {
									className: 'small',
									textContent: row.conn.caller_device_user_agent,
								}]);
							}
						}, {
							id: 'callee',
							name: _('Volaný'),
							formatCell: (td, v, row) => {
								td.gwReplaceChildren([{
									textContent: row.conn.callee,
								}, {
									':skip': !row.conn.callee_user_name,
									textContent: row.conn.callee_user_name,
								}, {
									className: 'small',
									textContent: row.conn.external === 'callee' || row.conn.external === 'both' ? _('Externí') : _('Interní'),
								}, {
									className: 'small',
									textContent: ((/@([^:]+)/.exec(row.conn.callee_device_uri || '')) || [])[1],
									title: row.conn.callee_device_uri,
								}, {
									className: 'small',
									textContent: row.conn.callee_device_user_agent,
								}]);
							}
						}, {
							id: 'label',
							name: _('Detail'),
							formatCell: (td, v, row) => {
								let s = [];
								const {conn, type} = row;
								if (type == 'dial') {
									if (conn.external === 'caller') {
										s.push(_('Vstup do ústředny'));
									}
								}
								if (type == 'queue_join') {
									s.push(_("Fronta") + ': ' + conn.queue + (conn.queue_name ? ` (${conn.queue_name})` : ''));
								}
								if (type == 'end') {
									if (conn.hangup_by) {
										s.push(_('Ukončil hovor: ') + Formats.call_hangup_by(conn.hangup_by));
									}
									let reason;
									switch (conn.hangup_cause) {
										case 'cancel':
										case '127':
											reason = _('Opuštěno volajícím');
											break;
										case 'noanswer':
											reason = _('Zrušeno ústřednou (bez odpovědi)');
											break;
										case 'followme':
											reason = _('Přepojeno přes followme');
											break;
										case 'parked':
											reason = _('Zaparkováno');
											break;
										case 'unparked':
											reason = _('Odparkováno');
											break;
										case 'transferred':
											reason = _('Přepojeno');
											break;
										case 'pickup':
											reason = _('Zvednuto pomocí "pickup" funkce na jiné lince');
											break;
										case '26':
											reason = _('Zvednuto na jiném zařízení');
											break;
										case '21':
										case '17':
										case '19':
											reason = _('Odmítnuto');
											break;
										case '34':
											reason = _('Obsazeno');
											break;
										case '16':
											reason = _('Standardní zavěšení');
											break;
										default:
											reason = _('Neznámý důvod (kód: {hangup_cause})').replace('{hangup_cause}', conn.hangup_cause);
											break;
									}
									s.push(_('Důvod ukončení: ') + reason);
								}
								td.gwReplaceChildren(s.map(s => ({
									textContent: s,
								})));
							},
						}];
					},
					rows: items,
				});
			},
		});
	},

	getDetailsColumn() {
		let me = this;

		const show_recordings = Application.getActiveUserGroups().some(g => !['regular', 'agent'].includes(g.member_role) || g.options.show_recordings);

		return {
			id: 'details',
			name: _('Detaily'),
			filter: {
				type: 'options',
				options: [{
					value: 'notes',
					title: _('Obsahuje poznámku'),
				}, {
					value: 'category',
					title: _('Obsahuje kategorii'),
				}, ...(show_recordings ? [{
					value: 'recordings',
					title: _('Obsahuje nahrávky'),
				}] : []), {
					value: 'nothing',
					title: _('Neobsahuje nic'),
				}],
				matches(val, filter_val, row) {
					if (filter_val.length === 4) {
						return true;
					}

					if (row.has_recordings && filter_val.includes('recordings')) {
						return true;
					}

					if (row.call_category && filter_val.includes('category')) {
						return true;
					}

					if (row.has_notes && filter_val.includes('notes')) {
						return true;
					}

					if (!row.has_recordings && !row.call_category && !row.has_notes && filter_val.includes('nothing')) {
						return true;
					}

					return false;
				},
			},
			formatCell(td, v, row) {
				me.renderCallIcons(td, row);
			},
			sortable: {
				type: 'string',
				value: (v, row) => {
					return [
						row.has_notes,
						row.call_category,
						row.has_recordings,
					].map(b => b ? '0' : '1').join();
				},
			},
		};
	},

	getRecordingAndVoicemailColumnForFullCdr() {
		let me = this;
		return {
			id: 'details',
			name: _('Nahrávky a záznamník'),
			filter: {
				type: 'options',
				options: [{
					value: 'recordings',
					title: _('Obsahuje nahrávky'),
				}, {
					value: 'voicemail',
					title: _('Obsahuje záznamník'),
				}, {
					value: 'nothing',
					title: _('Neobsahuje nahrávky ani záznamník'),
				}],
				matches: (val, filter_val, row) => {
					let matches = false;
					if (filter_val.includes('recordings')) {
						matches ||= row.has_recordings;
					}
					if (filter_val.includes('voicemail')) {
						matches ||= row.has_voicemails;
					}
					if (filter_val.includes('nothing')) {
						matches ||= !row.has_recordings && !row.has_voicemails;
					}
					return matches;
				},
			},
			formatCell(td, v, row) {
				me.renderCallIconRecordings(td, row);
				me.renderCallIconVoicemail(td, row);
			},
			sortable: {
				type: 'string',
				value: (v, row) => {
					switch (true) {
						case row.has_recordings && row.has_voicemails: return "3";
						case row.has_recordings: return "2";
						case row.has_voicemails: return "1";
						default: return "0";
					}
				},
			},
		};
	},

	/*
	 * PLUGIN: extension point for search
	 */
	renderToolbarSearchFields() {
		return [{
			xtype: 'Widgets.DateRangeInput',

			async fromParams(params) {
				let range = params.get('range') || 'today';

				await this.setRangeOption(range);

				if (range == 'custom') {
					this.setTimeRange(
						params.get('since'),
						params.get('until')
					);
				}
			},
			toParams(params) {
				let range = this.getRange();
				if (range == 'custom') {
					let [since, until] = this.getTimeRange();
					params.set('since', since || '');
					params.set('until', until || '');
					params.set('range', range);
				} else {
					params.delete('since');
					params.delete('until');
					params.set('range', range);
				}

				this.updateTimeFields();
			},
			toQuery(query) {
				let [since, until] = this.getTimeRange();

				query.since = since;
				query.until = until;
			},
		}, {
			xtype: 'TextField',
			extraClass: 'small',
			label: _('Telefon'),
			optional: true,

			fromParams(params) {
				this.setValue(params.get('number') || '');
			},
			toParams(params) {
				let number = this.getValue() || '';
				if (number) {
					params.set('number', number);
				} else {
					params.delete('number');
				}
			},
			toQuery(query) {
				query.extension = this.getValue() || undefined;
			},
		}, {
			xtype: 'TextField',
			extraClass: 'small',
			label: _('Vstupní fronta'),
			optional: true,

			fromParams(params) {
				this.setValue(params.get('queue') || '');
			},
			toParams(params) {
				let queue = this.getValue() || '';
				if (queue) {
					params.set('queue', queue);
				} else {
					params.delete('queue');
				}
			},
			toQuery(query) {
				query.queue = this.getValue() || undefined;
			},
		}];
	},

	renderToolbar() {
		return [...this.renderToolbarSearchFields(), {
			nodeName: 'button',
			type: 'submit',
			className: 'primary small',
			textContent: _('Zobrazit'),
		}, {
			nodeName: 'button',
			type: 'button',
			className: 'secondary small icon-only',
			children: [Utils.useIcon('settings')],
			'on:click': () => {
				this.cdrTableColumnsSelector.showDialog();
			},
		}, {
			nodeName: 'button',
			type: 'button',
			className: 'secondary small',
			textContent: _('Exportovat XLSX'),
			"on:click": () => {
				let params = new URLSearchParams(JSON.parse(JSON.stringify(this.buildQuery())));
				let group = Application.getActiveUserGroup();
				if (group) {
					params.set('active_user_group', group.id);
				}

				location.href = API_BASE + 'calls/export/xlsx?' + params.toString();
			},
		}];
	},

	// This button is rendered merely as a (non-visual) "space padding" element
	renderButtonPlaceholder: () => ({
		nodeName: 'button',
		className: 'small secondary icon-only vhidden',
		disabled: true,
	}),

	/*
	 * PLUGIN: extension point
	 */
	renderCallIcons(td, call) {
		td.gwCreateChild({
			nodeName: 'button',
			className: 'small secondary icon-only btn-notes',
			children: [Utils.useIcon(call.has_notes ? 'documentwithcontentfilled' : 'document')],
			title: call.has_notes ? _('Zobrazit poznámky') : _('Přidat poznámku'),
			'on:click': ev => this.showCallCard(call, td),
		});

		
		if (call.last_queue_has_category_hierarchy && call.result == 'connected') {
			td.gwCreateChild({
				nodeName: 'button',
				ref: 'btnCategory',
				className: 'small secondary icon-only  btn-category',
				children: [Utils.useIcon(call.call_category ? 'categoryfilled' : 'categoryempty')],
				title: call.call_category ? _('Zobrazit Kategorie') : _('Přidat Kategorie'),
				'on:click': ev => this.editCategory(call, ev.target.closest('tr')),
			});
		} else {
			td.gwCreateChild(this.renderButtonPlaceholder());
		}

		if (Application.getActiveUserGroups().some(g => !['regular', 'agent'].includes(g.member_role) || g.options.show_recordings)) {
			this.renderCallIconRecordings(td, call);
		}

		this.renderCallIconVoicemail(td, call);
	},
	
	renderCallIconRecordings(td, call) {
		if (call.has_recordings) {
			td.gwCreateChild({
				nodeName: 'button',
				className: 'small secondary icon-only',
				children: [Utils.useIcon('dialing')],
				title: call.recordings_readable ? _('Zobrazit nahrávky') : _('K nahrávkám nemáte přístup'),
				disabled: !call.recordings_readable,
				'on:click': ev => this.showRecordings(call),
			});
		} else {
			td.gwCreateChild(this.renderButtonPlaceholder());
		}
	},

	renderCallIconVoicemail(td, call) {
		if (call.has_voicemails) {
			td.gwCreateChild({
				nodeName: 'button',
				className: 'small secondary icon-only',
				children: [Utils.useIcon('voicemail')],
				title: _('Zobrazit nahrávky ze záznamníku'),
				'on:click': ev => this.showVoicemails(call),
			});
		} else {
			td.gwCreateChild(this.renderButtonPlaceholder());
		}
	},

	async editCategory(call, tr) {
		if (!call.last_queue) {
			Application.notify({
				kind: 'warning',
				text: _('Kategorii lze změnit jen u frontového hovoru.'),
			});
			return;
		}

		let hierarchy;
		try {
			hierarchy = await REST.GET('queues/' + encodeURIComponent(call.last_queue) + '/call-category-hierarchy');
		} catch (ex) {
			if (ex.status == 404) {
				Application.notify({
					kind: 'warning',
					text: _('Fronta {queue} nemá přiřazenu skupinu kategorií.')
						.replace('{queue}', call.last_queue),
				});
				return;
			}

			throw ex;
		}

		let dlg = new CDR.CallCategoryDialog({
			hierarchy,

			async onSave(data) {
				await REST.PUT('call-info/' + encodeURIComponent(call.id), data);

				let cats = this.getSelectedCategories();

				call.call_category = cats;

				let td_cat1 = tr.querySelector('td[data-col=category1]');
				if (td_cat1) {
					td_cat1.textContent = (cats && cats[0]) ? cats[0].name : '';
				}
				
				let td_cat2 = tr.querySelector('td[data-col=category2]');
				if (td_cat2) {
					td_cat2.textContent = (cats && cats[1]) ? cats[1].name : '';
				}
				
				tr.querySelector('td[data-col=details] .btn-category').gwReplaceChildren([
					Utils.useIcon(cats ? 'categoryfilled' : 'categoryempty')
				]);
			},
		});

		dlg.setFormFieldsData({
			category: call.call_category || [],
		});
	},

	async showCallCard(call, td) {
		const call_data = {
			call_id: call.id,
			phone: call.caller && call.caller.number,
		};

		// preload the data before showing the dialog to avoid UI flicker
		let data = await REST.GET('call-info/' + encodeURIComponent(call_data.call_id), {phone: call_data.phone});
		
		new Main.Dialog({
			title: _('Poznámky k hovoru'),

			'after:initComponent'() {
				let dlg = this;
				this.bodyEl.gwCreateChild({
					xtype: 'Office.CallPanel.CallCard',
					showCategory: false,
					showRightPane: false,
					call: call_data,
					'after:initComponent'() {
						this.setData(data);
					},
					afterNoteSaved() {
						// update icon after note is saved
						td.querySelector('.btn-notes').gwReplaceChildren([
							Utils.useIcon('documentwithcontentfilled')
						]);
					},
					'on:close'() {
						dlg.close();
					}
				});
			},
		});
	},
	
	async showRecordings(call) {
		let recordings = await REST.GET('calls/' + encodeURIComponent(call.id) + '/recordings');

		let dlg = new CDR.CallRecordingsDialog({
			call_id: call.id,
			recordingsBaseUrl: API_BASE + 'calls/' + encodeURIComponent(call.id) + '/recordings/',
		});

		dlg.setRecordings(recordings);
	},
	
	async showVoicemails(call) {
		let voicemails = await REST.GET('voicemails', {call_id: call.id});

		let dlg = new CDR.CallVoicemailsDialog();

		dlg.setVoicemails(voicemails);
	},

	/*
	 * Update URL query parameters from the input fields.
	 */
	updateParams() {
		Application.route({
			resolve: false,
			paramsFn: params => {
				for (let f of this.getLiveToolbarSearchFields()) {
					f.toParams(params);
				}

				return params;
			},
		});
	},

	maxCalls: 3000,

	buildQuery() {
		let query = {
			limit: this.maxCalls,
		};

		for (let f of this.getLiveToolbarSearchFields()) {
			f.toQuery(query);
		}

		if (this.full) {
			query.full = '1';
		}

		return query;
	},

	async reload() {
		let list = await REST.GET('calls', this.buildQuery());
		if (list.length >= this.maxCalls) {
			Application.notify({
				text: _('Pokoušíte se zobrazit více jak {max} hovorů, pro jejich získání použijte export do XLSX, nebo použijte jiný filtr.')
					.replace('{max}', this.maxCalls),
			});
		}

		for (let call of list) {
			
			call.source = [
				call.source_callback_case && _('Callback'),
				call.source_outbound_campaign && _('Kampaň') + ' ' + call.source_outbound_campaign.name
			].filter(s => s).join(', ');
			
			let cat = call.call_category;

			call.category1 = cat ? cat[0].name : '';
			call.category2 = (cat && cat[1]) ? cat[1].name : '';

			call.caller_name = call.caller && call.caller.name;
			call.callee_name = call.callee && call.callee.name;
			call.last_callee_name = call.last_callee && call.last_callee.name;

			call.caller_company = call.caller && call.caller.company;
			call.callee_company = call.callee && call.callee.company;
			call.last_callee_company = call.last_callee && call.last_callee.company;

			call.caller = call.caller && call.caller.number;
			call.callee = call.callee && call.callee.number;
			call.last_callee = call.last_callee && call.last_callee.number;
		}

		this.table.setData(list);
	},

	async applyParams(params) {
		for (let f of this.getLiveToolbarSearchFields()) {
			f.fromParams(params);
		}
	},
});

// }}}
// {{{ CDR.CallInfoScreen

GW.define('CDR.CallInfoScreen', 'Main.Screen', {
	title: _('CDR'),
	titleIcon: 'phone-calls-filled',

	'after:initComponent'() {
		this.bodyEl.gwCreateChild({
			className: 'content-container',
			children: [{
				nodeName: 'h2',
				textContent: _('Nahrávky'),
			}, {
				ref: 'table',
				xtype: 'CDR.CallRecordingsTable',
			}],
		}, this);
	},

	setData(call) {
		this.titleEl.textContent = _('Hovor') + ' ' + call.caller + ' -> ' + call.callee;

		this.table.recordingsBaseUrl = API_BASE + 'calls/' + encodeURIComponent(call.call_id) + '/recordings/';
		this.table.setData(call.recordings);
	},
});

// }}}
// {{{ Routes

Application.addRoutes([{
	group: 'auth',
	module: 'cdr',
	path: APP_BASE + 'cdr',
	async handler({params}) {
		let c = Application.show({
			id: 'cdr.history',
			xtype: 'CDR.HistoryScreen',
		});

		c.applyParams(params);
	}    
}, {
	group: 'auth',
	module: 'cdr',
	path: APP_BASE + 'cdr/call-info/{call_id:str}',
	async handler({params, captures}) {
		let call_id = captures.call_id;
		try {
			let call = await REST.GET('calls/' + encodeURIComponent(call_id));
			call.recordings = await REST.GET('calls/' + encodeURIComponent(call_id) + '/recordings');

			let c = Application.show({
				id: 'cdr.callinfo',
				xtype: 'CDR.CallInfoScreen',
			});

			c.setData(call);
		} catch (ex) {
			let c = Application.show({
				id: 'cdr.callinfo.notfound',
				xtype: 'Main.Screen',
				title: _('CDR'),
				titleIcon: 'phone-calls-filled',

				'after:initComponent'() {
					this.bodyEl.gwCreateChild({
						className: 'cdr-call-info-not-found',
						textContent: _('Hovor nebyl nalezen, nebo k němu nemáte přístup'),
					}, this);
				},
			});
		}
	}    
}]);

// }}}

Application.registerModule({
	id: 'cdr',
	title: _('CDR'),
	tagline: _('Historie hovorů ústředny'),
	icon: 'phone-calls',
	href: APP_BASE + 'cdr',
	all_groups_allowed: true,
});

// Full CDR

Application.addRoutes([{
	group: 'auth',
	module: 'fullcdr',
	path: APP_BASE + 'fullcdr',
	async handler({params}) {
		let c = Application.show({
			id: 'cdr.history',
			title: _('CDR Asterisk'),
			xtype: 'CDR.HistoryScreen',
			full: true,
		});

		c.applyParams(params);
	},
}]);

Application.registerModule({
	id: 'fullcdr',
	title: _('CDR Asterisk'),
	tagline: _('Úplná historie hovorů ústředny'),
	icon: 'phone-calls',
	href: APP_BASE + 'fullcdr',
});
